import {
  baseCdcEventDataSchema,
  cdcAuthContextSchema,
  cdcEventModule,
  cdcEventType,
} from '@tempo-io/common-dto';
import { discriminatedUnion, z } from 'zod';

export const itemStoreBaseCdcEventSchema = baseCdcEventDataSchema.extend({
  module: z.literal(cdcEventModule.enum['item-store']),
});

export const itemIdentifierSchema = z.object({
  id: z.number().int(),
});

// Using loose to allow additional properties for item entities for events
// that carry full or partial item data. Replace it with item Zod Schema when thats ready
const itemEntitySchema = itemIdentifierSchema
  .extend({
    doneDateTime: z.string().nullish(),
  })
  .loose();

export const itemDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: itemEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: itemEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.updated_partial),
    // Intentionally loose: consumers ignore partial events, but processMessage validates
    // all SQS messages before routing, so the schema must accept any partial payload
    // shape (e.g. doneDateTime as a FieldChange object) without throwing.
    change: itemIdentifierSchema.loose(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: itemIdentifierSchema,
  }),
]);

export const integrationStatusSchema = z.enum([
  'CREATED',
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'COMPLETED_WITH_PENDING',
  'PARTIAL_FAILURE',
  'FAILED',
  'DELETING',
]);

export const synchronizationStatusSchema = z.enum(['ENABLED', 'DISABLED', 'DELETING']);

export const sourceSystemTypeSchema = z.enum(['JIRA', 'ADO']);

export const integrationIdentifierSchema = z.object({
  id: z.number().int(),
});

export const integrationEntitySchema = z
  .object({
    id: z.number().int(),
    status: integrationStatusSchema,
    synchronizationStatus: synchronizationStatusSchema.optional(),
    sourceSystemType: sourceSystemTypeSchema.optional(),
    lastPullSyncAt: z.string().nullish(),
    lastPushSyncAt: z.string().nullish(),
    excludedTeamCount: z.number().int().optional(),
    excludedTeams: z.array(z.object({ teamName: z.string(), loopName: z.string() })).optional(),
  })
  .loose();

export const integrationDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: integrationEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: integrationEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: integrationIdentifierSchema,
  }),
]);

export const loopIdentifierSchema = z.object({
  id: z.number().int(),
});

export const loopDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('loop'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: loopIdentifierSchema.loose(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('loop'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: loopIdentifierSchema.loose(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('loop'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: loopIdentifierSchema,
  }),
]);

export const dependencyEntitySchema = z.object({
  id: z.number().int(),
  predecessorId: z.number().int(),
  successorId: z.number().int(),
  waitDays: z.number().int().nullish(),
  status: z.string().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  createdBy: z.string(),
  updatedBy: z.string(),
});

export const dependencyDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('dependency'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: dependencyEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('dependency'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: dependencyEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('dependency'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: dependencyEntitySchema,
  }),
]);

export const estimationDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const estimationSchemeDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation-scheme'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation-scheme'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation-scheme'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemStatusDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-status'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-status'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-status'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemTypeDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemTypeLevelDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type-level'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type-level'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type-level'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);
export const itemFinancialsDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-financials'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
]);

const scheduleCdcEntitySchema = z.object({
  itemId: z.number().int(),
});

const scheduleDeleteCdcEntitySchema = z.union([
  scheduleCdcEntitySchema,
  z.object({ id: z.number().int() }).transform(({ id }) => ({ itemId: id })),
]);

export const scheduleDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('schedule'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: scheduleCdcEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('schedule'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: scheduleCdcEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('schedule'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: scheduleDeleteCdcEntitySchema,
  }),
]);

export const fieldDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const fieldValueDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field-value'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field-value'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field-value'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const picklistOptionDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('picklist-option'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('picklist-option'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('picklist-option'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const objectiveDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('objective'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('objective'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('objective'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemObjectiveDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-objective'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-objective'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-objective'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemKeyResultDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-key-result'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-key-result'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-key-result'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const keyResultDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('key-result'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('key-result'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('key-result'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

const loopScheduleCdcEntitySchema = z.object({
  loopId: z.number().int(),
  itemId: z.number().int().optional(),
});

export const loopScheduleDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('loop-schedule'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: loopScheduleCdcEntitySchema,
  }),
]);

export const demandDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const demandStatusDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-status'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-status'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-status'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const demandProposedItemDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-proposed-item'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-proposed-item'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-proposed-item'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const demandObjectiveDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-objective'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-objective'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('demand-objective'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemStoreEntityDataChangeDTOSchema = discriminatedUnion('entityType', [
  itemDataChangeDTOSchema,
  integrationDataChangeDTOSchema,
  dependencyDataChangeDTOSchema,
  estimationDataChangeDTOSchema,
  estimationSchemeDataChangeDTOSchema,
  itemStatusDataChangeDTOSchema,
  itemTypeDataChangeDTOSchema,
  itemTypeLevelDataChangeDTOSchema,
  itemFinancialsDataChangeDTOSchema,
  loopDataChangeDTOSchema,
  scheduleDataChangeDTOSchema,
  fieldDataChangeDTOSchema,
  fieldValueDataChangeDTOSchema,
  picklistOptionDataChangeDTOSchema,
  objectiveDataChangeDTOSchema,
  itemObjectiveDataChangeDTOSchema,
  itemKeyResultDataChangeDTOSchema,
  keyResultDataChangeDTOSchema,
  loopScheduleDataChangeDTOSchema,
  demandDataChangeDTOSchema,
  demandStatusDataChangeDTOSchema,
  demandProposedItemDataChangeDTOSchema,
  demandObjectiveDataChangeDTOSchema,
]);

export const itemStoreDataChangeDTOSchema = z.object({
  data: baseCdcEventDataSchema.and(itemStoreEntityDataChangeDTOSchema),
  authContext: cdcAuthContextSchema,
  metadata: z.record(z.string(), z.string()).optional(),
});

export type ItemStoreDataChangeDTO = z.output<typeof itemStoreDataChangeDTOSchema>;

export type ItemDataChangeDTO = Extract<ItemStoreDataChangeDTO['data'], { entityType: 'item' }>;
export type ItemDeleteDataChangeDTO = Extract<ItemDataChangeDTO, { eventType: 'deleted' }>;
